import type { EditorHistory } from './types';

const MAX_SNAPSHOTS = 30;

function cloneFallback(value: unknown, seen: WeakMap<object, unknown>): unknown {
  if (value === null || typeof value !== 'object') return value;

  const existing = seen.get(value);
  if (existing) return existing;

  if (typeof ImageData !== 'undefined' && value instanceof ImageData) {
    const clone = new ImageData(
      new Uint8ClampedArray(value.data),
      value.width,
      value.height,
    );
    seen.set(value, clone);
    return clone;
  }

  if (Array.isArray(value)) {
    const clone: unknown[] = [];
    seen.set(value, clone);
    for (const item of value) clone.push(cloneFallback(item, seen));
    return clone;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    const name = prototype?.constructor?.name ?? 'unknown';
    throw new TypeError(`Unsupported editor snapshot prototype: ${name}`);
  }

  const clone = Object.create(prototype) as Record<PropertyKey, unknown>;
  seen.set(value, clone);

  for (const key of Reflect.ownKeys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor) continue;
    if ('value' in descriptor) {
      descriptor.value = cloneFallback(descriptor.value, seen);
    }
    Object.defineProperty(clone, key, descriptor);
  }

  return clone;
}

function cloneSnapshot<T>(snapshot: T): T {
  if (typeof structuredClone === 'function') return structuredClone(snapshot);
  return cloneFallback(snapshot, new WeakMap()) as T;
}

export function createEditorHistory<T>(initial: T): EditorHistory<T> {
  let snapshots = [cloneSnapshot(initial)];
  let cursor = 0;

  return {
    push(snapshot) {
      snapshots = snapshots.slice(0, cursor + 1);
      snapshots.push(cloneSnapshot(snapshot));
      if (snapshots.length > MAX_SNAPSHOTS) snapshots.shift();
      cursor = snapshots.length - 1;
    },
    undo() {
      cursor = Math.max(0, cursor - 1);
      return cloneSnapshot(snapshots[cursor]);
    },
    redo() {
      cursor = Math.min(snapshots.length - 1, cursor + 1);
      return cloneSnapshot(snapshots[cursor]);
    },
    reset(snapshot) {
      snapshots = [cloneSnapshot(snapshot)];
      cursor = 0;
    },
    current() {
      return cloneSnapshot(snapshots[cursor]);
    },
  };
}
